<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import Heading from '$lib/components/primitives/Heading.svelte';
	import Text from '$lib/components/primitives/Text.svelte';
	import Grid from '$lib/components/layout/Grid.svelte';

	const features = $derived([
		{
			icon: 'clock',
			title: $_('products.web.features.timeTracking.title'),
			description: $_('products.web.features.timeTracking.description')
		},
		{
			icon: 'folder',
			title: $_('products.web.features.projects.title'),
			description: $_('products.web.features.projects.description')
		},
		{
			icon: 'users',
			title: $_('products.web.features.team.title'),
			description: $_('products.web.features.team.description')
		},
		{
			icon: 'file-text',
			title: $_('products.web.features.invoicing.title'),
			description: $_('products.web.features.invoicing.description')
		},
		{
			icon: 'bar-chart',
			title: $_('products.web.features.analytics.title'),
			description: $_('products.web.features.analytics.description')
		},
		{
			icon: 'link',
			title: $_('products.web.features.integrations.title'),
			description: $_('products.web.features.integrations.description')
		}
	]);

	const benefits = $derived([
		{
			title: $_('products.web.benefits.accessible.title'),
			description: $_('products.web.benefits.accessible.description'),
			icon: 'globe'
		},
		{
			title: $_('products.web.benefits.realtime.title'),
			description: $_('products.web.benefits.realtime.description'),
			icon: 'zap'
		},
		{
			title: $_('products.web.benefits.secure.title'),
			description: $_('products.web.benefits.secure.description'),
			icon: 'shield'
		}
	]);
</script>

<svelte:head>
	<title>{$_('products.web.meta.title')}</title>
	<meta name="description" content={$_('products.web.meta.description')} />
	<meta property="og:title" content="{$_('products.web.meta.title')}" />
	<meta property="og:description" content={$_('products.web.meta.description')} />
</svelte:head>

<Header />

<div class="product-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			<div class="hero__grid">
				<div class="hero__content">
					<div class="hero__badge">
						<Icon name="globe" size={16} />
						<span>{$_('products.web.badge')}</span>
					</div>
					<h1 class="hero__title">
						{$_('products.web.hero.title')}
					</h1>
					<p class="hero__subtitle">
						{$_('products.web.hero.subtitle')}
					</p>
					<div class="hero__actions">
						<Button href="https://app.heyweek.com" variant="primary" size="lg">
							{$_('products.web.hero.primaryButton')}
						</Button>
						<Button href="/pricing" variant="ghost" size="lg">
							{$_('products.web.hero.secondaryButton')}
						</Button>
					</div>
				</div>
				<div class="hero__visual">
					<div class="hero__image"></div>
				</div>
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section class="features">
		<div class="container">
			<div class="features__header">
				<h2 class="features__title">{$_('products.web.features.title')}</h2>
				<p class="features__subtitle">{$_('products.web.features.subtitle')}</p>
			</div>
			
			<div class="features__grid">
				{#each features as feature}
					<Card class="feature-card">
						<div class="feature-card__icon">
							<Icon name={feature.icon} size={24} style="color: rgb(255, 94, 126);" />
						</div>
						<h3 class="feature-card__title">
							{feature.title}
						</h3>
						<p class="feature-card__description">
							{feature.description}
						</p>
					</Card>
				{/each}
			</div>
		</div>
	</section>

	<!-- Benefits Section -->
	<section class="benefits">
		<div class="container">
			<div class="benefits__grid">
				<div class="benefits__visual">
					<div class="benefits__image"></div>
				</div>
				<div class="benefits__content">
					<h2 class="benefits__title">
						{$_('products.web.benefits.title')}
					</h2>
					{#each benefits as benefit}
						<div class="benefit">
							<div class="benefit__icon">
								<Icon name={benefit.icon} size={20} style="color: rgb(255, 94, 126);" />
							</div>
							<div class="benefit__text">
								<h3 class="benefit__title">
									{benefit.title}
								</h3>
								<p class="benefit__description">
									{benefit.description}
								</p>
							</div>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('products.web.cta.title')}</h2>
				<p class="cta__subtitle">{$_('products.web.cta.subtitle')}</p>
				<div class="cta__actions">
					<Button href="https://app.heyweek.com" variant="primary" size="lg">
						{$_('products.web.cta.primaryButton')}
					</Button>
					<Button href="/products/mobile" variant="secondary" size="lg">
						{$_('products.web.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="products-web" />

<Footer />

<style>
	.product-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	/* Override Tailwind's container with proper constraints */
	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
	}

	.hero__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .hero__badge {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	.hero__visual {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.hero__image {
		aspect-ratio: 4/3;
		width: 100%;
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.1), rgba(255, 120, 150, 0.1));
		border-radius: var(--radius-xl);
		border: 1px solid rgba(255, 94, 126, 0.2);
		box-shadow: var(--shadow-elevated);
	}

	:global(.dark) .hero__image {
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.15), rgba(255, 120, 150, 0.15));
		border-color: rgba(255, 94, 126, 0.3);
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
	}

	.features__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.features__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.features__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.features__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-6);
	}

	:global(.feature-card) {
		padding: var(--space-8);
		transition: all 0.2s ease;
		border: 1px solid var(--border-light);
	}

	:global(.feature-card:hover) {
		transform: translateY(-4px);
		border-color: var(--border-brand);
		box-shadow: var(--shadow-elevated);
	}

	:global(.dark .feature-card) {
		border-color: var(--border-default);
	}

	:global(.dark .feature-card:hover) {
		border-color: var(--border-brand);
	}

	.feature-card__icon {
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .feature-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.feature-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.feature-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Benefits Section */
	.benefits {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.benefits__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
	}

	.benefits__visual {
		order: 2;
	}

	.benefits__content {
		order: 1;
	}

	.benefits__image {
		aspect-ratio: 4/3;
		width: 100%;
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.1), rgba(255, 120, 150, 0.1));
		border-radius: var(--radius-xl);
		border: 1px solid rgba(255, 94, 126, 0.2);
	}

	:global(.dark) .benefits__image {
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.15), rgba(255, 120, 150, 0.15));
		border-color: rgba(255, 94, 126, 0.3);
	}

	.benefits__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-8);
	}

	.benefit {
		display: flex;
		gap: var(--space-4);
		margin-bottom: var(--space-6);
	}

	.benefit__icon {
		flex-shrink: 0;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .benefit__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.benefit__title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-1);
	}

	.benefit__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
		text-align: center;
	}

	:global(.dark) .cta {
		background: linear-gradient(180deg, transparent 0%, var(--bg-tertiary) 100%);
	}

	.cta__content {
		text-align: center;
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive Design */
	@media (max-width: 968px) {
		.hero__grid,
		.benefits__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.benefits__visual {
			order: 1;
		}

		.benefits__content {
			order: 2;
		}
	}

	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.features__grid {
			grid-template-columns: 1fr;
		}
	}
</style>